import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import Typewriter from 'typewriter-effect'
import { Map, MapMarker, MarkerContent, useMap } from '../../components/ui/map'
import PageLayout from '../../components/PageLayout'
import { useTheme } from '../../contexts/ThemeContext'
import { useTravelCountries } from '../../hooks/useTravelCountries'
import { introdata, traveldata } from '../../content_option'
import './style.css'

// Degrees of longitude the globe drifts per second while idle.
const SPIN_DEG_PER_SEC = 3.5
// Stop drifting once the reader has zoomed in past the "whole globe" view.
const SPIN_MAX_ZOOM = 4
// How long after a map gesture before the globe resumes drifting.
const IDLE_RESUME_MS = 5000
// Grace period so sweeping down the list doesn't fire a fly per row.
const HOVER_FLY_DELAY_MS = 150

// Used when a config row omits its own zoom.
const DEFAULT_COUNTRY_ZOOM = 4.5

// Shaded outlines, generated from the country list by
// scripts/generate-country-shapes.js.
const SHAPES_URL = `${process.env.PUBLIC_URL ?? ''}/travel-countries.geojson`
const SHAPES_SOURCE = 'travel-countries'
const SHAPES_FILL = 'travel-countries-fill'
const SHAPES_LINE = 'travel-countries-line'

const ACCENT = '#cc0000'

// Fill is kept low so the basemap's coastlines and labels still read through;
// the country under the cursor or currently open lifts out of the rest.
const fillOpacity = (highlighted) => [
  'case',
  ['==', ['get', 'code'], highlighted ?? ''],
  0.45,
  0.16,
]

const lineOpacity = (highlighted) => [
  'case',
  ['==', ['get', 'code'], highlighted ?? ''],
  1,
  0.5,
]

const lineWidth = (highlighted) => [
  'case',
  ['==', ['get', 'code'], highlighted ?? ''],
  1.6,
  0.8,
]

/**
 * Paints the visited countries onto the basemap. Lives inside <Map> so it can
 * reach the MapLibre instance; `isLoaded` from useMap flips back to false when
 * the theme swaps the style, which drops custom layers, so this re-adds them.
 */
const CountryShapes = ({ highlighted, onSelect }) => {
  const { map, isLoaded } = useMap()

  useEffect(() => {
    if (!isLoaded || !map) return

    if (!map.getSource(SHAPES_SOURCE)) {
      map.addSource(SHAPES_SOURCE, { type: 'geojson', data: SHAPES_URL })
    }

    // Slip the fill under the basemap's labels so place names stay readable.
    const firstLabel = map
      .getStyle()
      ?.layers?.find((layer) => layer.type === 'symbol')?.id

    map.addLayer(
      {
        id: SHAPES_FILL,
        type: 'fill',
        source: SHAPES_SOURCE,
        paint: { 'fill-color': ACCENT, 'fill-opacity': fillOpacity(highlighted) },
      },
      firstLabel
    )

    map.addLayer(
      {
        id: SHAPES_LINE,
        type: 'line',
        source: SHAPES_SOURCE,
        layout: { 'line-join': 'round' },
        paint: {
          'line-color': ACCENT,
          'line-width': lineWidth(highlighted),
          'line-opacity': lineOpacity(highlighted),
        },
      },
      firstLabel
    )

    return () => {
      try {
        if (map.getLayer(SHAPES_LINE)) map.removeLayer(SHAPES_LINE)
        if (map.getLayer(SHAPES_FILL)) map.removeLayer(SHAPES_FILL)
        if (map.getSource(SHAPES_SOURCE)) map.removeSource(SHAPES_SOURCE)
      } catch {
        // The style may already have torn the layers down.
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoaded, map])

  // Repaint on highlight rather than re-adding layers.
  useEffect(() => {
    if (!isLoaded || !map || !map.getLayer(SHAPES_FILL)) return
    map.setPaintProperty(SHAPES_FILL, 'fill-opacity', fillOpacity(highlighted))
    map.setPaintProperty(SHAPES_LINE, 'line-opacity', lineOpacity(highlighted))
    map.setPaintProperty(SHAPES_LINE, 'line-width', lineWidth(highlighted))
  }, [isLoaded, map, highlighted])

  // Clicking anywhere in a country selects it, not just its marker.
  useEffect(() => {
    if (!isLoaded || !map) return

    const handleClick = (e) => {
      const code = e.features?.[0]?.properties?.code
      if (code) onSelect(code)
    }
    const enter = () => {
      map.getCanvas().style.cursor = 'pointer'
    }
    const leave = () => {
      map.getCanvas().style.cursor = ''
    }

    map.on('click', SHAPES_FILL, handleClick)
    map.on('mouseenter', SHAPES_FILL, enter)
    map.on('mouseleave', SHAPES_FILL, leave)

    return () => {
      map.off('click', SHAPES_FILL, handleClick)
      map.off('mouseenter', SHAPES_FILL, enter)
      map.off('mouseleave', SHAPES_FILL, leave)
      map.getCanvas().style.cursor = ''
    }
  }, [isLoaded, map, onSelect])

  return null
}

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches

/**
 * Zoom at which the globe sits comfortably inside its pane. MapLibre renders
 * the world 512 * 2^zoom px wide, so the sphere's diameter is that over PI;
 * solve back from the pane's short side to keep the globe framed on a phone
 * and on a wide desktop alike.
 */
const overviewZoom = () => {
  if (typeof window === 'undefined') return 1.6
  const narrow = window.innerWidth < 992
  const paneWidth = narrow ? window.innerWidth : window.innerWidth * 0.7
  const paneHeight = narrow
    ? Math.max(380, window.innerHeight * 0.6)
    : window.innerHeight
  const diameter = Math.min(paneWidth, paneHeight) * 0.78
  return Math.max(0.9, Math.log2((diameter * Math.PI) / 512))
}

export const Travel = () => {
  const { theme } = useTheme()
  const { countries, overviewCenter } = useTravelCountries()

  const [map, setMap] = useState(null)
  const [activeKey, setActiveKey] = useState(null)
  const [hoverKey, setHoverKey] = useState(null)
  const [flyTarget, setFlyTarget] = useState(null)
  const [interacting, setInteracting] = useState(false)

  const reducedMotion = useMemo(prefersReducedMotion, [])
  const idleTimer = useRef(null)
  const itemRefs = useRef({})

  // The globe drifts only while nothing else is competing for attention.
  const spinning = !reducedMotion && !interacting && !activeKey && !hoverKey

  const markInteraction = useCallback(() => {
    setInteracting(true)
    clearTimeout(idleTimer.current)
    idleTimer.current = setTimeout(() => setInteracting(false), IDLE_RESUME_MS)
  }, [])

  useEffect(() => () => clearTimeout(idleTimer.current), [])

  // Pause the drift on any direct gesture against the map.
  useEffect(() => {
    if (!map) return
    const gestures = [
      'mousedown',
      'touchstart',
      'wheel',
      'dragstart',
      'rotatestart',
      'pitchstart',
      'boxzoomstart',
    ]
    gestures.forEach((event) => map.on(event, markInteraction))
    return () => gestures.forEach((event) => map.off(event, markInteraction))
  }, [map, markInteraction])

  useEffect(() => {
    if (!map || !spinning) return
    let frame
    let last = performance.now()

    const step = (now) => {
      const delta = (now - last) / 1000
      last = now
      if (map.getZoom() < SPIN_MAX_ZOOM) {
        const center = map.getCenter()
        map.setCenter([center.lng + SPIN_DEG_PER_SEC * delta, center.lat])
      }
      frame = requestAnimationFrame(step)
    }

    frame = requestAnimationFrame(step)
    return () => cancelAnimationFrame(frame)
  }, [map, spinning])

  const resetView = useCallback(() => {
    if (!map) return
    map.flyTo({
      center: overviewCenter,
      zoom: overviewZoom(),
      duration: reducedMotion ? 0 : 1200,
      essential: true,
    })
  }, [map, overviewCenter, reducedMotion])

  useEffect(() => {
    if (!map || !flyTarget) return
    const country = countries.find((c) => c.key === flyTarget)
    if (!country) return

    const timer = setTimeout(() => {
      map.flyTo({
        center: [country.lng, country.lat],
        zoom: country.zoom ?? DEFAULT_COUNTRY_ZOOM,
        duration: reducedMotion ? 0 : 1400,
        essential: true,
      })
    }, HOVER_FLY_DELAY_MS)

    return () => clearTimeout(timer)
  }, [map, flyTarget, countries, reducedMotion])

  const selectCountry = useCallback(
    (country, { scrollIntoView = false } = {}) => {
      if (activeKey === country.key) {
        setActiveKey(null)
        setFlyTarget(null)
        resetView()
        return
      }
      setActiveKey(country.key)
      setFlyTarget(country.key)
      if (scrollIntoView) {
        itemRefs.current[country.key]?.scrollIntoView({
          block: 'nearest',
          behavior: reducedMotion ? 'auto' : 'smooth',
        })
      }
    },
    [activeKey, resetView, reducedMotion]
  )

  const selectByCode = useCallback(
    (code) => {
      const country = countries.find((c) => c.key === code)
      if (country) selectCountry(country, { scrollIntoView: true })
    },
    [countries, selectCountry]
  )

  const renderRail = () => {
    if (!countries.length) return <p className='travel-status'>Nothing mapped yet.</p>

    return (
      <>
        <div className='travel-stats'>
          <span>{countries.length} countries</span>
        </div>

        <ul className='travel-list list'>
          {countries.map((country) => (
            <li
              key={country.key}
              ref={(el) => {
                itemRefs.current[country.key] = el
              }}
              className={`travel-item${activeKey === country.key ? ' is-active' : ''}${
                hoverKey === country.key ? ' is-hover' : ''
              }`}
            >
              <button
                type='button'
                className='travel-place'
                onClick={() => selectCountry(country)}
                onMouseEnter={() => {
                  setHoverKey(country.key)
                  setFlyTarget(country.key)
                }}
                onMouseLeave={() => setHoverKey(null)}
                onFocus={() => {
                  setHoverKey(country.key)
                  setFlyTarget(country.key)
                }}
                onBlur={() => setHoverKey(null)}
              >
                <span className='travel-place-name'>{country.name}</span>
              </button>
            </li>
          ))}
        </ul>
      </>
    )
  }

  return (
    <PageLayout
      title='Travel'
      description={traveldata.description.first}
      id='travel'
      className='travel'
    >
      <div className='intro_sec travel_sec d-block d-lg-flex align-items-center'>
        <div className='travel_globe order-1 order-lg-2'>
          <Map
            ref={setMap}
            theme={theme}
            projection={{ type: 'globe' }}
            center={overviewCenter}
            zoom={overviewZoom()}
            minZoom={0.8}
            maxZoom={9}
            dragRotate={false}
            className='travel-map-canvas'
          >
            <CountryShapes
              highlighted={hoverKey ?? activeKey}
              onSelect={selectByCode}
            />

            {countries.map((country) => {
              const isActive = activeKey === country.key
              const isHover = hoverKey === country.key

              return (
                <MapMarker
                  key={country.key}
                  longitude={country.lng}
                  latitude={country.lat}
                  onClick={() => selectCountry(country, { scrollIntoView: true })}
                  onMouseEnter={() => setHoverKey(country.key)}
                  onMouseLeave={() => setHoverKey(null)}
                >
                  <MarkerContent className='travel-marker-content'>
                    <div
                      className={`travel-marker${isActive ? ' is-active' : ''}${
                        isHover ? ' is-hover' : ''
                      }`}
                    >
                      <span className='travel-marker-dot' />
                      <span className='travel-marker-label'>{country.name}</span>
                    </div>
                  </MarkerContent>
                </MapMarker>
              )
            })}
          </Map>

          <div className='travel-zoom'>
            <button
              type='button'
              aria-label='Zoom in'
              onClick={() => {
                markInteraction()
                map?.zoomTo(map.getZoom() + 1, { duration: 300 })
              }}
            >
              +
            </button>
            <button
              type='button'
              aria-label='Zoom out'
              onClick={() => {
                markInteraction()
                map?.zoomTo(map.getZoom() - 1, { duration: 300 })
              }}
            >
              &minus;
            </button>
            <button
              type='button'
              aria-label='Reset view'
              onClick={() => {
                setActiveKey(null)
                setFlyTarget(null)
                resetView()
              }}
            >
              &#9673;
            </button>
          </div>
        </div>

        <div className='text order-2 order-lg-1 h-100 d-lg-flex justify-content-center'>
          <div className='intro mx-auto'>
            <Link to='/' className='title mb-1x'>
              {introdata.title}
            </Link>
            <div className='mb-1x'>
              <Typewriter
                options={{
                  strings: [
                    traveldata.description.first,
                    traveldata.description.second,
                    traveldata.description.third,
                  ],
                  autoStart: true,
                  loop: true,
                  deleteSpeed: 10,
                }}
              />
            </div>
            {renderRail()}
          </div>
        </div>
      </div>
    </PageLayout>
  )
}
